import currency from 'currency.js';
import solver from 'solver-utilities';
import { shopify } from './shopify.js';
import { error } from './notification.js';
import { TYPES } from './types';

let messageTimeout = null;

/**
 * Sets Cart UI
 */
const updateCartUI = async function (cart) {
	const carts = document.querySelectorAll('[data-cart]');

	for (const _cart of carts) {
		let section = _cart.getAttribute('data-cart');

		const req = await fetch('/?sections=' + section);

		if (req.error) {
			error(req.error.message ? req.error.message : req.error);
			return;
		}

		let markup = await req.json();
		markup = markup[section];
		markup = new DOMParser().parseFromString(markup, 'text/html');
		markup = markup.querySelector(`[data-cart="${section}"]`).innerHTML;

		const quantities = document.querySelectorAll('[data-el="cart-quantity"]');
		if (quantities && quantities.length) {
			quantities.forEach((quantity) => {
        if (cart.item_count > 0){
          quantity.classList.remove('hide')
        } else {
          quantity.classList.add('hide')
        }
        quantity.innerText = cart.item_count
    });
		}

		_cart.innerHTML = markup;
	}
};

export const open = async function (event) {
	if (event) event.preventDefault();

	const delay = 10;
	const container = document.querySelector('[data-el="header-cart"]');
  	const modal = document.querySelector('[data-el="cart-modal"]');

	if (!container || !modal) return;

	container.classList.add('header-cart--active');
  	modal.classList.add('cart-flyout--active');

  	await solver.each.asyncTimeout(delay);
  	document.body.style.overflow = "hidden";

  	modal.classList.add('animate__fadeIn');

	await solver.each.asyncTimeout(delay);
	container.classList.add('animate__fadeInRight');
};

export const close = async function (event) {
	if (event) event.preventDefault();

	const delay = 10;
	const container = document.querySelector('[data-el="header-cart"]');
  	const modal = document.querySelector('[data-el="cart-modal"]');
	if (!container) return;

	container.classList.add('animate__fadeOutRight');
	document.body.style.overflow = "unset";
	await solver.each.asyncTimeout(delay);
  	modal.classList.add('animate__fadeOut');
  	await solver.each.asyncTimeout(delay);
  	modal.classList.remove('cart-flyout--active', 'animate__fadeOut', 'animate__fadeIn');
	container.classList.remove(
		'header-cart--active',
		'animate__fadeInRight',
		'animate__fadeOutRight'
	);
};

/**
 * Gets cart items and updates quick view of quantites
 * @param {object} event - DOM event
 */
export const get = async function (event, updateUI = true) {
	if (event) event.preventDefault();

	const req = await shopify.cart.get();

	if (!req || (req && req.error)) {
		error(req.error.message ? req.error.message : req.error);
		return;
	}

	if (updateUI) updateCartUI(req.data);
	window.Solver.cart = req.data;

	return { data: req.data };
};

/**
 * Adds line item to cart
 * @param {object} event - DOM event
 * @param {number} variantID - ID of the variant to add
 * @param {quantity} quantity - amount of the variant to add
 * @param {object} properties - lineitem properties
 * @param {object} attributes - cart attributes key values dictionary
 */
export const add = async function (
	event,
	variantID,
	quantity,
	properties = null,
	attributes = null
) {
	if (event) event.preventDefault();

	let req = await shopify.cart.add(variantID, quantity, properties, attributes);

	if (!req) {
		error(TYPES.GENERIC);
		return { error: TYPES.GENERIC };
	}

	if (req && req.error) {
		error(req.error.message ? req.error.message : req.error);
		return { error: req.error };
	}

	req = await get();

	updateCartUI(req.data);

	return { data: req.data };
};

/**
 * Adds line item to cart
 * @param {object} event - DOM event
 * @param {number} variantID - ID of the variant to add
 * @param {quantity} quantity - amount of the variant to add
 * @param {object} properties - lineitem properties
 * @param {object} attributes - cart attributes key values dictionary
 */
export const update = async function (
	event,
	variantID,
	quantity,
	properties = null,
	attributes = null
) {
	if (event) event.preventDefault();

	let req = await shopify.cart.update(variantID, quantity, properties, attributes);

	if (!req) {
		error(TYPES.GENERIC);
		return { error: TYPES.GENERIC };
	}

	if (req && req.error) {
		error(req.error.message ? req.error.message : req.error);
		return { error: req.error };
	}

	return { data: req.data };
};

/**
 * Updates line item in cart - then updates subsequent cart UI
 * @param {object} event - DOM event
 * @param {number|string} id (variant_id|key) - ID of the variant(number) to add | Line item key(string)
 * @param {quantity} quantity - amount of the variant to add
 * @param {object} properties - line item property key values dictionary
 * @param {object} attributes - cart attributes key values dictionary
 */
const change = async function (event, id, quantity = 0, properties = null, attributes = null) {
	if (!id) return;

	const obj = {
		id: id.toString(),
		quantity: parseInt(quantity)
	};

	if (properties) obj.properties = properties;

	const req = await shopify.cart.change(obj, attributes);

	if (req.error) {
		error(req.error.message ? req.error.message : req.error);
		return;
	}

	updateCartUI(req.data);

	return { data: req.data };
};

/**
 * Removes line item in cart - then updates subsequent cart UI
 * @param {object} event - DOM event
 * @param {number} variantID - ID of the variant to add
 */
const remove = async function (event, variantID) {
	if (!variantID) return;

	const req = await shopify.cart.change({
		id: variantID.toString(),
		quantity: 0
	});

	if (req.error) {
		error(req.error.message ? req.error.message : req.error);
		return;
	}

	updateCartUI(req.data);

	return { data: req.data };
};

/**
 * Sets Gift Message to cart attributes
 * @param { obj } event - user generated event
 */
const setGiftwrapMessge = async function (event) {
	if (!event) return;
	event.preventDefault();

	const form = document.querySelector('[data-form="giftwrap"]');
	let textarea = event.target;

	if (!window.Solver || (window.Solver && !window.Solver.cart)) {
		return;
	}

	if (messageTimeout) clearTimeout(messageTimeout);

	messageTimeout = setTimeout(async () => {
		if (messageTimeout) clearTimeout(messageTimeout);
		messageTimeout = null;

		const attributes = Object.assign({}, window.Solver.cart.attributes);
		if (textarea.value) {
			attributes['Gift Order'] = 'True';
		} else {
			attributes['Gift Order'] = '';
		}
		attributes['Gift Message'] = textarea.value;

		await update(event, null, null, null, attributes);
	}, 500);
};

export const cart = async function () {
	window.Solver.openHeaderCart = open;
	window.Solver.closeHeaderCart = close;
	window.Solver.getCart = get;
	window.Solver.addToCart = add;
	window.Solver.changeLineItem = change;
	window.Solver.removeLineItem = remove;
	window.Solver.setGiftwrapMessge = setGiftwrapMessge;
};
